import { useState } from 'react';
import { Link } from 'react-router-dom';
import { usePages } from '@/hooks';
import { Card, CardHeader, CardTitle, CardContent, Button, Skeleton } from '@/components/ui';
import { formatRelativeTime } from '@/lib/utils';
import { Plus, Eye, Lock, Users, Pin, Globe } from 'lucide-react';
import { useAuthStore } from '@/stores';

export function PageList() {
  const [page, setPage] = useState(1);
  const { data, isLoading } = usePages({ page, perPage: 20 });
  const { user, isAuthenticated } = useAuthStore();

  const getVisibilityIcon = (visibility: string) => {
    switch (visibility) {
      case 'anonymous':
        return <Globe className="h-4 w-4" />;
      case 'public':
        return <Eye className="h-4 w-4" />;
      case 'editors':
        return <Users className="h-4 w-4" />;
      case 'private':
        return <Lock className="h-4 w-4" />;
      default:
        return <Eye className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">所有页面</h1>
        {isAuthenticated && (user?.role === 'editor' || user?.role === 'admin') && (
          <Link to="/pages/new">
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              新建页面
            </Button>
          </Link>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-6 w-1/2" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full mb-2" />
                <Skeleton className="h-4 w-3/4" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : data?.items.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <p>暂无页面</p>
            {isAuthenticated && (user?.role === 'editor' || user?.role === 'admin') && (
              <Link to="/pages/new" className="text-primary hover:underline mt-2 inline-block">
                创建第一个页面
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {data?.items.map((page) => (
            <Link key={page.id} to={`/pages/${page.slug}`}>
              <Card className="hover:border-primary transition-colors">
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg hover:text-primary flex items-center gap-2">
                      {page.title}
                      {page.is_pinned && (
                        <Pin className="h-4 w-4 text-primary" />
                      )}
                    </CardTitle>
                    <div className="flex items-center text-muted-foreground">
                      {getVisibilityIcon(page.visibility)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {page.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-muted-foreground">
                    <div className="flex items-center space-x-4">
                      <span>作者: {page.author.username}</span>
                      <span>更新于 {formatRelativeTime(page.updated_at)}</span>
                    </div>
                    {page.tags && page.tags.length > 0 && (
                      <div className="flex items-center space-x-1">
                        {page.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag.id}
                            className="px-2 py-0.5 bg-muted rounded text-xs"
                          >
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-center space-x-2">
          <Button
            variant="outline"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
          >
            上一页
          </Button>
          <span className="text-sm text-muted-foreground">
            第 {page} / {data.totalPages} 页
          </span>
          <Button
            variant="outline"
            disabled={page === data.totalPages}
            onClick={() => setPage(page + 1)}
          >
            下一页
          </Button>
        </div>
      )}
    </div>
  );
}
